import axios from 'axios';
import { CommonState, error, isFetching } from '../common';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { enqueueRequestErrorSnackbar } from '../../actions/enqueueSnackbar';
import { IConnection, ItemRequestParams, Order } from '../../types/activeConnections.';
import { itemsPerPage } from '../../constants/activeConnections';
import { OdataResponse } from '@pec/aion-ui-odata/types/odataResponse';
import { QueryBuilder } from '@pec/aion-ui-odata/builders/odataQueryBuilder';
import { RequestCallbacksParams } from '../../types';
import { updateOrganizationProfileHiringClientsNotifications } from '../../actions/changeOrganizationProfileNotifications';

const name = 'activeConnections';

export type FetchConnectionsParams = RequestCallbacksParams & {
  organizationId: string;
  page: number;
  order: Order;
  orderBy: string;
};

export type FetchConnectionsParamsV302 = Omit<FetchConnectionsParams, 'order'>;

export interface ActiveConnectionsResponseV302 {
  total: number;
  values: IConnection[];
}

type FetchParams = {
  orderBy: string;
};

export const fetchAllActiveConnections = createAsyncThunk(
  `${name}/fetchAllConnections`,
  async (organizationId: string, { dispatch }) => {
    try {
      const sortOrder = `dateAdded asc`;
      const count = 100;
      const value = [];
      let countRecords = 0;
      let page = 1;

      do {
        const params = new QueryBuilder()
          .orderBy(sortOrder)
          .top(count)
          .skipByPage(page.toString(), count)
          .toQueryParam();

        const { data } = await axios.get<OdataResponse<IConnection[]>>(
          `/api/v3.1/organizations/${organizationId}/activeConnections`,
          { params }
        );
        countRecords = data['@odata.count'];
        value.push(...data.value);
        page++;
      } while (countRecords > (page - 1) * count);

      return value;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export const fetchActiveConnections = createAsyncThunk(
  `${name}/fetch`,
  async ({ organizationId, page, order, orderBy, afterSuccess }: FetchConnectionsParams, { dispatch }) => {
    try {
      const sortOrder = orderBy && order ? `${orderBy} ${order}` : undefined;
      const params = new QueryBuilder()
        .orderBy(sortOrder)
        .top(itemsPerPage)
        .skipByPage(page.toString(), itemsPerPage)
        .toQueryParam();

      const { data } = await axios.get<OdataResponse<IConnection[]>>(
        `/api/v3.1/organizations/${organizationId}/activeConnections`,
        {
          params
        }
      );
      afterSuccess && afterSuccess();
      return { ...data, page, order, orderBy };
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export const fetchActiveConnectionsDataV302 = async (
  organizationId: string,
  params: FetchParams
): Promise<ActiveConnectionsResponseV302> => {
  const { data } = await axios.get<ActiveConnectionsResponseV302>(
    `/api/v3.02/organizations/${organizationId}/activeConnections`,
    {
      params
    }
  );
  return data;
};

export const fetchActiveConnectionsV302 = createAsyncThunk(
  `${name}/fetchV302`,
  async ({ organizationId, page, orderBy, afterSuccess }: FetchConnectionsParamsV302, { dispatch }) => {
    try {
      const params = {
        orderBy,
        skip: (page - 1) * itemsPerPage,
        take: itemsPerPage
      };

      const { data } = await axios.get<ActiveConnectionsResponseV302>(
        `/api/v3.02/organizations/${organizationId}/activeConnections`,
        {
          params
        }
      );

      afterSuccess && afterSuccess();
      return {
        ...data,
        orderBy,
        page
      };
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export const fetchFirstActiveConnectionV302ForFlagCheck = createAsyncThunk(
  `${name}/fetchV302Baseline`,
  async (organizationId: string, { dispatch }) => {
    try {
      const params = {
        skip: 0,
        take: 1
      };

      const { data } = await axios.get<ActiveConnectionsResponseV302>(
        `/api/v3.02/organizations/${organizationId}/activeConnections`,
        { params }
      );
      return data.values;
    } catch (error) {
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

type DeleteParams = ItemRequestParams<Partial<IConnection>>;

export const deleteConnection = createAsyncThunk(
  `${name}/delete`,
  async ({ item, afterSuccess, afterError }: DeleteParams, { dispatch }) => {
    try {
      await axios.delete(`/api/v3.1/activeConnections/${item.id}`);
      afterSuccess && afterSuccess();
      item.contractorId && dispatch(updateOrganizationProfileHiringClientsNotifications(item.contractorId));
      return item;
    } catch (error) {
      afterError && afterError();
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

export type CancelConnectionParams = {
  contractorCompanyId: number;
  hiringClientCompanyId: number;
  contractorId: string;
} & RequestCallbacksParams;

export const cancelConnection = createAsyncThunk(
  `${name}/cancel`,
  async (
    { contractorCompanyId, hiringClientCompanyId, contractorId, afterSuccess, afterError }: CancelConnectionParams,
    { dispatch }
  ) => {
    try {
      await axios.post('/api/v1/billing/Connection/Cancel', { contractorCompanyId, hiringClientCompanyId });
      afterSuccess && afterSuccess();
      dispatch(updateOrganizationProfileHiringClientsNotifications(contractorId));
    } catch (error) {
      afterError && afterError();
      dispatch(enqueueRequestErrorSnackbar());
      throw error;
    }
  }
);

// If the first connection has selected services, we assume all do,
// as the list is sorted with incomplete connections at the top.
export const areAllServiceGroupsCompleted = (data: IConnection[]): boolean => {
  if (data.length === 0) return true;
  return (
    Array.isArray(data[0].selectedServices) &&
    data[0].selectedServices.length > 0 &&
    data[0].selectedServices[0].serviceGroups.length > 0
  );
};

export type State = CommonState & {
  total: number;
  page: number;
  order: Order;
  orderBy: string;
  activeConnections: IConnection[];
  allActiveConnections: IConnection[];
  areAllServiceGroupsCompleted: boolean;
  isActiveConnectionsPolling: boolean;
};

export const initialState: State = {
  total: 0,
  page: 1,
  order: 'asc',
  orderBy: '',
  error: null,
  isFetching: false,
  activeConnections: [],
  allActiveConnections: [],
  areAllServiceGroupsCompleted: false,
  isActiveConnectionsPolling: false
};

const activeConnections = createSlice({
  name,
  initialState,
  reducers: {
    setIsActiveConnectionsPolling: (state, action) => {
      state.isActiveConnectionsPolling = action.payload;
    },
    setAreAllServiceGroupsCompleted: (state, action) => {
      state.areAllServiceGroupsCompleted = areAllServiceGroupsCompleted(action.payload);
    },
    removeActiveConnectionOptimistic: (state, action) => {
      state.activeConnections = state.activeConnections.filter(connection => connection.id !== action.payload);
      state.areAllServiceGroupsCompleted = areAllServiceGroupsCompleted(state.activeConnections);
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchActiveConnections.pending, isFetching);
    builder.addCase(fetchActiveConnections.fulfilled, (state, action) => {
      state.activeConnections = action.payload.value;
      state.total = action.payload['@odata.count'];
      state.orderBy = action.payload.orderBy;
      state.order = action.payload.order;
      state.page = action.payload.page;
      state.isFetching = false;
    });
    builder.addCase(fetchActiveConnections.rejected, error);
    builder.addCase(fetchActiveConnectionsV302.fulfilled, (state, action) => {
      state.activeConnections = action.payload.values;
      state.total = action.payload.total;
      state.orderBy = action.payload.orderBy;
      state.page = action.payload.page;
      state.isFetching = false;
    });
    builder.addCase(fetchActiveConnectionsV302.pending, isFetching);
    builder.addCase(fetchActiveConnectionsV302.rejected, error);
    builder.addCase(fetchFirstActiveConnectionV302ForFlagCheck.fulfilled, (state, action) => {
      state.areAllServiceGroupsCompleted = areAllServiceGroupsCompleted(action.payload);
    });
    builder.addCase(fetchFirstActiveConnectionV302ForFlagCheck.pending, isFetching);
    builder.addCase(fetchFirstActiveConnectionV302ForFlagCheck.rejected, error);
    builder.addCase(deleteConnection.pending, state => {
      state.error = null;
    });
    builder.addCase(deleteConnection.rejected, error);
    builder.addCase(cancelConnection.pending, state => {
      state.error = null;
    });
    builder.addCase(cancelConnection.rejected, error);
    builder.addCase(fetchAllActiveConnections.pending, isFetching);
    builder.addCase(fetchAllActiveConnections.rejected, error);
    builder.addCase(fetchAllActiveConnections.fulfilled, (state, action) => {
      state.allActiveConnections = action.payload;
      state.isFetching = false;
    });
  }
});

export const {
  setIsActiveConnectionsPolling,
  setAreAllServiceGroupsCompleted,
  removeActiveConnectionOptimistic
} = activeConnections.actions;
export const { reducer: activeConnectionsReducer } = activeConnections;
